import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import SubCard from "../../../ui-component/cards/SubCard";
import {
  Autocomplete,
  Badge,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  Tabs,
  Tab,
  TextField,
  Paper,
  Typography,
} from "@mui/material";
import axios from "axios";
import { TableRow } from "@material-ui/core";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FilterListIcon from "@mui/icons-material/FilterList";
import IconButton from "@mui/material/IconButton";
import moment from "moment";
import { numericCommaSeparation } from "../../../ui-component/NumericSeperation";
import { LoadingButton } from "@material-ui/lab";
import { getDecryptDataFromLocal } from "../../../utils/encrypt";
import DeliveryCenterChatDrawer from "./DeliveryCenterChatDrawer";
import ShipshopyChatDrawer from "./ShipshopyChatDrawer";
import Button from "@mui/material/Button";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  DELIVERY_CENTER_EXTERNAL_USERS,
  SCM_TEAM_PHONE_NUMBERS,
} from "./deliveryCenterExternalAccess";

const DISPOSITION_OPTIONS = [
  { value: 1, label: "Chance of RTO", color: "#e53935", textColor: "#fff" },
  { value: 2, label: "Delivered", color: "#43a047", textColor: "#fff" },
  { value: 3, label: "Re-attempt", color: "#fdd835", textColor: "#000" },
  { value: 4, label: "Reached At Destination", color: "#fb8c00", textColor: "#fff" },
  { value: 5, label: "Out For Delivery", color: "#9ccc65", textColor: "#000" },
];

const DISPOSITION_BY_VALUE = DISPOSITION_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

// Multi-select filter options for the Disposition column — "empty" is the Excel-style
// "Not Set" bucket, kept as its own checkbox alongside the real disposition values.
const DISPOSITION_FILTER_OPTIONS = ["empty", ...DISPOSITION_OPTIONS.map((opt) => opt.value)];
const dispositionFilterLabel = (v) => (v === "empty" ? "Not Set" : DISPOSITION_BY_VALUE[v]?.label || v);

const DISPOSITION_EDIT_USERS = ["9315481432", "7011576531", "7048952168", "9310278575", "8368969041", "9811068834", "9625766488", "8882083014"];
const RAD_EDIT_USERS = ["7011576531", "7048952168"];
const DASHBOARD_LOCK_USERS = ["9811068834", "7011576531"];

// Shipshopy disposition — separate field from the internal Disposition above, shown only to
// SCM + the two external Shipshopy users (see deliveryCenterExternalAccess.js). External users
// view only; SCM (incl. Harshit) edits.
const SHIPSHOPY_DISPOSITION_OPTIONS = [
  { value: 1, label: "Re-attempt", color: "#fdd835", textColor: "#000" },
  { value: 2, label: "Hold", color: "#9e9e9e", textColor: "#fff" },
  { value: 3, label: "RTO", color: "#e53935", textColor: "#fff" },
  { value: 4, label: "RTO by Shipshopy", color: "#fb8c00", textColor: "#fff" },
  { value: 5, label: "Delivered", color: "#43a047", textColor: "#fff" },
];

const SHIPSHOPY_DISPOSITION_BY_VALUE = SHIPSHOPY_DISPOSITION_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

const SHIPSHOPY_DISPOSITION_FILTER_OPTIONS = ["empty", ...SHIPSHOPY_DISPOSITION_OPTIONS.map((opt) => opt.value)];
const shipshopyDispositionFilterLabel = (v) => (v === "empty" ? "Not Set" : SHIPSHOPY_DISPOSITION_BY_VALUE[v]?.label || v);

// Chat-pending filter, shared shape by both the internal "Action" column and the
// Shipshopy Chat column — selecting both options (or neither) is a no-op filter.
const CHAT_PENDING_FILTER_OPTIONS = ["pending", "none"];
const chatPendingFilterLabel = (v) => (v === "pending" ? "Pending Chat" : "No Pending Chat");

const COURIER_STATUS_OPTIONS = ["At Delivery Center", "Undelivered", "Out For Delivery"];

const ORDER_TYPE_OPTIONS = ["B2B", "B2C"];

// "Shipped By" column — courier the order went out with. Options come from the server
// (ShipShopy's external users never get RocketBox); this is the fallback list.
const SHIPPED_BY_OPTIONS = [
  { value: "SHIPSHOPY", label: "ShipShopy" },
  { value: "ROCKETBOX", label: "RocketBox" },
  { value: "PORTER", label: "Porter" },
];

const CSV_HEADERS = [
  { label: "S No.", key: "sNo" },
  { label: "Type", key: "type" },
  { label: "Shipped By", key: "shippedBy" },
  { label: "Reference ID", key: "referenceId" },
  { label: "AWB Number", key: "awbNumber" },
  { label: "Order Create Date", key: "orderCreatedAt" },
  { label: "Courier Status", key: "courierStatusText" },
  { label: "Customer Name", key: "customerName" },
  { label: "Customer Phone", key: "customerPhone" },
  { label: "Invoice Amount", key: "invoiceAmount" },
  { label: "COD Amount", key: "codAmount" },
  { label: "RAD", key: "rad" },
  { label: "Ageing", key: "ageing" },
  { label: "Shipping Cost (RTO Risk)", key: "shippingCostRtoFlag" },
  { label: "Agent Name", key: "agentName" },
  { label: "Manager", key: "managerName" },
  { label: "Verified By", key: "verifiedByName" },
  { label: "Disposition", key: "disposition" },
  { label: "Review", key: "review" },
  { label: "Last Message Date", key: "lastMessageDate" },
];

// Shipshopy CSV fields — appended for SCM (full report) or used standalone for the two external
// users (who never get the internal Agent/Manager/Disposition/Review/Last Message fields above).
const SHIPSHOPY_CSV_FIELDS = [
  { label: "Shipshopy Disposition", key: "shipshopyDisposition" },
  { label: "Shipshopy Last Message", key: "shipshopyLastMessage" },
];

// Fields hidden from the two external users' CSV export — internal agent/manager identity and
// the internal chat, same set of columns hidden from them in the table itself.
const CSV_FIELDS_HIDDEN_FROM_EXTERNAL = ["shippingCostRtoFlag", "agentName", "managerName", "verifiedByName", "disposition", "review", "lastMessageDate"];

// CSV export: keep every visible field, but instead of dumping the whole chat
// thread, use a "Review" column with just the most recent SCM message.
function mapOrderToCsvRow(order, index) {
  return {
    sNo: index + 1,
    type: order.orderTypeLabel || "",
    shippedBy: order.shippedBy || "",
    referenceId: order.zohoInvoice?.zohoInvoiceNumber || "",
    awbNumber: order.awbNumber || "",
    orderCreatedAt: order.createdAt ? moment(order.createdAt).format("DD/MM/YYYY hh:mm A") : "",
    courierStatusText: order.courierStatusText || "",
    customerName: order.address?.name || order.name || "",
    customerPhone: order.address?.phoneNumber || order.phoneNumber || "",
    invoiceAmount: Math.round(order.totalPayableAmount || 0),
    codAmount: Math.round(order.codAmount || 0),
    rad: order.atDeliveryCenterDate ? moment(order.atDeliveryCenterDate).format("DD/MM/YYYY") : "",
    ageing: order.ageingDays != null ? order.ageingDays : "",
    shippingCostRtoFlag: order.shippingCostRtoFlag != null ? Math.round(order.shippingCostRtoFlag) : "",
    agentName: order.agentName || "",
    managerName: order.managerName || "",
    verifiedByName: order.verifiedByName || "",
    disposition: DISPOSITION_BY_VALUE[order.deliveryCenterDisposition]?.label || "Not Set",
    review: order.lastScmMessage || "",
    lastMessageDate: order.lastMessageAt ? moment(order.lastMessageAt).format("DD/MM/YYYY hh:mm A") : "",
    // ShipShopy-only fields stay blank for RocketBox orders
    shipshopyDisposition: order.isShipShopy === false ? "" : SHIPSHOPY_DISPOSITION_BY_VALUE[order.shipshopyDisposition]?.label || "Not Set",
    shipshopyLastMessage: order.isShipShopy === false ? "" : order.shipshopyLastMessageText || "",
  };
}

function DispositionCell({ order, disabled, onUpdated }) {
  const [value, setValue] = useState(order.deliveryCenterDisposition || "");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/updateDeliveryCenterOrderField`, {
        orderId: order._id,
        field: "disposition",
        value: newValue,
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          onUpdated();
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  return (
    <Select
      size="small"
      fullWidth
      value={value}
      disabled={disabled || loading}
      onChange={handleChange}
      displayEmpty
      renderValue={(selected) => {
        if (!selected) return <Chip size="small" label="Not Set" />;
        const opt = DISPOSITION_BY_VALUE[selected];
        return (
          <Chip
            size="small"
            label={opt.label}
            style={{ backgroundColor: opt.color, color: opt.textColor, fontWeight: 500 }}
          />
        );
      }}
    >
      <MenuItem value="">
        <em>Not Set</em>
      </MenuItem>
      {DISPOSITION_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          <Chip
            size="small"
            label={opt.label}
            style={{ backgroundColor: opt.color, color: opt.textColor, fontWeight: 500 }}
          />
        </MenuItem>
      ))}
    </Select>
  );
}

function ShipshopyDispositionCell({ order, disabled, onUpdated }) {
  const [value, setValue] = useState(order.shipshopyDisposition || "");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/updateDeliveryCenterOrderField`, {
        orderId: order._id,
        field: "shipshopyDisposition",
        value: newValue,
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          onUpdated();
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  return (
    <Select
      size="small"
      fullWidth
      value={value}
      disabled={disabled || loading}
      onChange={handleChange}
      displayEmpty
      renderValue={(selected) => {
        if (!selected) return <Chip size="small" label="Not Set" />;
        const opt = SHIPSHOPY_DISPOSITION_BY_VALUE[selected];
        return (
          <Chip
            size="small"
            label={opt.label}
            style={{ backgroundColor: opt.color, color: opt.textColor, fontWeight: 500 }}
          />
        );
      }}
    >
      <MenuItem value="">
        <em>Not Set</em>
      </MenuItem>
      {SHIPSHOPY_DISPOSITION_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          <Chip
            size="small"
            label={opt.label}
            style={{ backgroundColor: opt.color, color: opt.textColor, fontWeight: 500 }}
          />
        </MenuItem>
      ))}
    </Select>
  );
}

function RadCell({ order, editable, onUpdated }) {
  const [value, setValue] = useState(
    order.atDeliveryCenterDate ? moment(order.atDeliveryCenterDate).format("YYYY-MM-DD") : ""
  );
  const [loading, setLoading] = useState(false);

  if (!editable) {
    return order.atDeliveryCenterDate ? moment(order.atDeliveryCenterDate).format("DD/MM/YYYY") : "";
  }

  const handleUpdate = () => {
    if (!value) return;
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/updateDeliveryCenterOrderField`, {
        orderId: order._id,
        field: "rad",
        value,
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          onUpdated();
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  return (
    <Grid container spacing={1} alignItems="center" style={{ minWidth: 190 }}>
      <Grid item xs={7}>
        <TextField size="small" type="date" fullWidth value={value} onChange={(e) => setValue(e.target.value)} />
      </Grid>
      <Grid item xs={5}>
        <LoadingButton size="small" variant="contained" loading={loading} onClick={handleUpdate}>
          Update
        </LoadingButton>
      </Grid>
    </Grid>
  );
}

function DashboardLockCell({ order, editable, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const locked = !!order.deliveryCenterDashboardLock;

  const handleToggle = () => {
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/updateDeliveryCenterOrderField`, {
        orderId: order._id,
        field: "dashboardLock",
        value: !locked,
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          onUpdated();
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  if (!editable) {
    return locked ? <Chip size="small" label="Locked" style={{ backgroundColor: "#e53935", color: "#fff" }} /> : "";
  }

  return (
    <LoadingButton
      size="small"
      variant={locked ? "contained" : "outlined"}
      color={locked ? "error" : "primary"}
      loading={loading}
      onClick={handleToggle}
    >
      {locked ? "Unlock" : "Lock"}
    </LoadingButton>
  );
}

// Shared checkbox-menu multi-select filter used by any table header that needs an
// Excel-style "pick several values" filter (Ageing, Courier Status, ...).
function MultiSelectFilterHeader({ label, options, selected, onApply, extraControl, getLabel = (v) => v }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pending, setPending] = useState(selected);

  const open = (e) => {
    setPending(selected);
    setAnchorEl(e.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const toggleValue = (v) => {
    setPending((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  return (
    <Grid container alignItems="center" wrap="nowrap">
      <Grid item>{label}</Grid>
      {extraControl}
      <Grid item>
        <IconButton size="small" onClick={open} color={selected.length ? "primary" : "default"}>
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Grid>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <div style={{ maxHeight: 300, overflowY: "auto", padding: "0 8px" }}>
          {options.length === 0 && (
            <MenuItem disabled>
              <em>No values</em>
            </MenuItem>
          )}
          {options.map((v) => (
            <MenuItem key={v} dense onClick={() => toggleValue(v)}>
              <FormControlLabel
                onClick={(e) => e.stopPropagation()}
                control={<Checkbox size="small" checked={pending.includes(v)} onChange={() => toggleValue(v)} />}
                label={getLabel(v)}
              />
            </MenuItem>
          ))}
        </div>
        <Grid container spacing={1} style={{ padding: 8 }}>
          <Grid item xs={6}>
            <Button
              size="small"
              fullWidth
              onClick={() => {
                setPending([]);
                onApply([]);
                close();
              }}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => {
                onApply(pending);
                close();
              }}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Menu>
    </Grid>
  );
}

function AgeingFilterHeader({ sortByAgeing, onToggleSort, ageingOptions, selectedAgeing, onApply }) {
  return (
    <MultiSelectFilterHeader
      label="Ageing"
      options={ageingOptions}
      selected={selectedAgeing}
      onApply={onApply}
      extraControl={
        <Grid item>
          <IconButton size="small" onClick={onToggleSort}>
            {sortByAgeing === "desc" ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
          </IconButton>
        </Grid>
      }
    />
  );
}

function DeliveryCenterOrdersTable({ onlyRto }) {
  const phoneNumber = getDecryptDataFromLocal("phoneNumber");

  // Disposition is only editable from the "All Orders" tab — once an order is
  // showing in the RTO Orders tab it's read-only here (server-enforced too,
  // see updateDeliveryCenterOrderField in orderService.js).
  const canEditDisposition = !onlyRto && DISPOSITION_EDIT_USERS.includes(phoneNumber);
  const canEditRad = RAD_EDIT_USERS.includes(phoneNumber);
  const canToggleDashboardLock = DASHBOARD_LOCK_USERS.includes(phoneNumber);

  // Shipshopy external users (out-of-company courier partner) see this table with the
  // internal agent/manager identity + internal chat hidden, and their own Shipshopy
  // disposition/chat columns instead. SCM team sees both sets of columns. Everyone else is
  // unaffected. See deliveryCenterExternalAccess.js.
  const isExternalUser = DELIVERY_CENTER_EXTERNAL_USERS.includes(phoneNumber);
  const isScmTeam = SCM_TEAM_PHONE_NUMBERS.includes(phoneNumber);
  const canEditShipshopyDisposition = isScmTeam;

  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [awbNumber, setAwbNumber] = useState("");
  const [courierStatusFilter, setCourierStatusFilter] = useState([]);
  const [managerNameFilter, setManagerNameFilter] = useState([]);
  const [agentName, setAgentName] = useState(null);
  const [dispositionFilter, setDispositionFilter] = useState([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState("");
  const [sortByAgeing, setSortByAgeing] = useState("");
  const [pendingChatFilter, setPendingChatFilter] = useState("");
  const [dashboardLockFilter, setDashboardLockFilter] = useState("");
  const [ageingValues, setAgeingValues] = useState([]);
  const [verifiedByFilter, setVerifiedByFilter] = useState([]);
  const [shipshopyDispositionFilter, setShipshopyDispositionFilter] = useState([]);
  const [shipshopyPendingChatFilter, setShipshopyPendingChatFilter] = useState([]);
  const [shippedByFilter, setShippedByFilter] = useState([]);

  const [managerOptions, setManagerOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [ageingOptions, setAgeingOptions] = useState([]);
  const [courierStatusOptions, setCourierStatusOptions] = useState(COURIER_STATUS_OPTIONS);
  const [verifiedByOptions, setVerifiedByOptions] = useState([]);
  const [shippedByOptions, setShippedByOptions] = useState(SHIPPED_BY_OPTIONS);

  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chatOrderId, setChatOrderId] = useState(null);
  const [shipshopyChatOrderId, setShipshopyChatOrderId] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const csvLinkRef = useRef(null);

  const buildPayload = (overrides = {}) => ({
    customerName,
    phoneNumber: phone,
    invoiceNumber: invoiceId,
    awbNumber,
    courierStatusText: courierStatusFilter,
    managerName: managerNameFilter,
    agentName,
    dispositionFilter,
    orderTypeFilter,
    sortByAgeing,
    pendingChatFilter,
    dashboardLockFilter,
    ageingValues,
    verifiedByNames: verifiedByFilter,
    shipshopyDispositionFilter,
    shipshopyPendingChatFilter,
    shippedByFilter,
    onlyRto,
    ...overrides,
  });

  const handleSearch = (cp = page, limit = rowsPerPage, overrides = {}) => {
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/getDeliveryCenterOrders`, {
        ...buildPayload(overrides),
        page: cp,
        limit,
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setData(res.data.data?.data || []);
          setTotalDocuments(res.data.data?.metaData?.totalDocuments || 0);
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  useEffect(() => {
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/getDeliveryCenterFilterOptions`, { onlyRto })
      .then((res) => {
        if (res.data.success) {
          setAgentOptions(res.data.data?.agents || []);
          setManagerOptions(res.data.data?.managers || []);
          setAgeingOptions(res.data.data?.ageingValues || []);
          setCourierStatusOptions(res.data.data?.courierStatusOptions || COURIER_STATUS_OPTIONS);
          setVerifiedByOptions(res.data.data?.verifiedByOptions || []);
          setShippedByOptions(res.data.data?.shippedByOptions || SHIPPED_BY_OPTIONS);
        }
      })
      .catch((err) => console.log(err));

    handleSearch(0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyRto]);

  const handleDownloadCsv = () => {
    setCsvLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/getDeliveryCenterOrders`, {
        ...buildPayload(),
        page: 0,
        limit: totalDocuments || 10000,
      })
      .then((res) => {
        setCsvLoading(false);
        if (res.data.success) {
          const rows = res.data.data?.data || [];
          setCsvData(rows.map(mapOrderToCsvRow));
          setTimeout(() => csvLinkRef.current?.link.click(), 100);
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setCsvLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleSearch(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = +event.target.value;
    setRowsPerPage(newLimit);
    setPage(0);
    handleSearch(0, newLimit);
  };

  // Generic helper for header-embedded filters: updates state and re-runs the
  // search immediately (these filters don't wait for the Search button).
  const updateHeaderFilter = (setter, key, value) => {
    setter(value);
    setPage(0);
    handleSearch(0, rowsPerPage, { [key]: value });
  };

  const toggleAgeingSort = () => {
    const next = sortByAgeing === "desc" ? "asc" : "desc";
    updateHeaderFilter(setSortByAgeing, "sortByAgeing", next);
  };

  // External users get only the base + Shipshopy fields (no internal agent/manager/old
  // disposition/old chat). SCM gets everything. Everyone else is unaffected.
  const csvHeaders = isExternalUser
    ? [...CSV_HEADERS.filter((h) => !CSV_FIELDS_HIDDEN_FROM_EXTERNAL.includes(h.key)), ...SHIPSHOPY_CSV_FIELDS]
    : isScmTeam
    ? [...CSV_HEADERS, ...SHIPSHOPY_CSV_FIELDS]
    : CSV_HEADERS;

  return (
    <>

      <Grid container spacing={2} alignItems="center">
        <Grid item lg={3} md={3}>
          <TextField
            label="Customer Name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField label="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField label="Invoice Id" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} fullWidth />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField label="AWB Number" value={awbNumber} onChange={(e) => setAwbNumber(e.target.value)} fullWidth />
        </Grid>
        <Grid item lg={3} md={3}>
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth
            loading={loading}
            loadingIndicator="Loading..."
            onClick={() => {
              setPage(0);
              handleSearch(0, rowsPerPage);
            }}
          >
            Search
          </LoadingButton>
        </Grid>
        <Grid item lg={3} md={3}>
          <LoadingButton
            variant="outlined"
            color="primary"
            fullWidth
            loading={csvLoading}
            loadingIndicator="Preparing..."
            onClick={handleDownloadCsv}
          >
            Download CSV
          </LoadingButton>
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`delivery-center-orders-${onlyRto ? "rto" : "all"}-${moment().format("YYYY-MM-DD")}.csv`}
            ref={csvLinkRef}
            style={{ display: "none" }}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper} style={{ marginTop: 20 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>S No.</TableCell>
              <TableCell>
                <Grid container alignItems="center" wrap="nowrap">
                  <Grid item>Type</Grid>
                  <Grid item>
                    <Select
                      size="small"
                      variant="standard"
                      displayEmpty
                      value={orderTypeFilter}
                      onChange={(e) => updateHeaderFilter(setOrderTypeFilter, "orderTypeFilter", e.target.value)}
                      style={{ marginLeft: 8, minWidth: 60 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {ORDER_TYPE_OPTIONS.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </TableCell>
              <TableCell style={{ minWidth: 150 }}>
                <MultiSelectFilterHeader
                  label="Shipped By"
                  options={shippedByOptions.map((o) => o.value)}
                  selected={shippedByFilter}
                  onApply={(vals) => updateHeaderFilter(setShippedByFilter, "shippedByFilter", vals)}
                  getLabel={(v) => shippedByOptions.find((o) => o.value === v)?.label || v}
                />
              </TableCell>
              <TableCell>Reference ID</TableCell>
              <TableCell>AWB Number</TableCell>
              <TableCell>Order Create Date</TableCell>
              <TableCell style={{ minWidth: 200 }}>
                <MultiSelectFilterHeader
                  label="Courier Status"
                  options={courierStatusOptions}
                  selected={courierStatusFilter}
                  onApply={(vals) => updateHeaderFilter(setCourierStatusFilter, "courierStatusText", vals)}
                />
              </TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell>Customer Phone</TableCell>
              <TableCell>Invoice Amount</TableCell>
              <TableCell>COD Amount</TableCell>
              <TableCell>RAD</TableCell>
              <TableCell>
                <AgeingFilterHeader
                  sortByAgeing={sortByAgeing}
                  onToggleSort={toggleAgeingSort}
                  ageingOptions={ageingOptions}
                  selectedAgeing={ageingValues}
                  onApply={(vals) => updateHeaderFilter(setAgeingValues, "ageingValues", vals)}
                />
              </TableCell>
              {!isExternalUser && (
                <>
                  <TableCell style={{ minWidth: 150 }}>Shipping Cost (RTO Risk)</TableCell>
                  <TableCell style={{ minWidth: 180 }}>
                    <div>Agent Name</div>
                    <Autocomplete
                      size="small"
                      options={agentOptions}
                      getOptionLabel={(option) => (option ? `${option.name} (${option.userName})` : "")}
                      isOptionEqualToValue={(option, value) => option?._id === value?._id}
                      value={agentOptions.find((a) => a.name === agentName) || null}
                      onChange={(e, v) => updateHeaderFilter(setAgentName, "agentName", v ? v.name : null)}
                      renderInput={(params) => <TextField {...params} variant="standard" placeholder="Search agent" />}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: 180 }}>
                    <MultiSelectFilterHeader
                      label="Manager"
                      options={managerOptions}
                      selected={managerNameFilter}
                      onApply={(vals) => updateHeaderFilter(setManagerNameFilter, "managerName", vals)}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: 180 }}>
                    <MultiSelectFilterHeader
                      label="Verified By"
                      options={verifiedByOptions}
                      selected={verifiedByFilter}
                      onApply={(vals) => updateHeaderFilter(setVerifiedByFilter, "verifiedByNames", vals)}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: 180 }}>
                    <MultiSelectFilterHeader
                      label="Disposition"
                      options={DISPOSITION_FILTER_OPTIONS}
                      selected={dispositionFilter}
                      onApply={(vals) => updateHeaderFilter(setDispositionFilter, "dispositionFilter", vals)}
                      getLabel={dispositionFilterLabel}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: 140 }}>
                    <div>Dashboard Lock</div>
                    <Select
                      size="small"
                      variant="standard"
                      displayEmpty
                      fullWidth
                      value={dashboardLockFilter}
                      onChange={(e) => updateHeaderFilter(setDashboardLockFilter, "dashboardLockFilter", e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="locked">Locked</MenuItem>
                      <MenuItem value="unlocked">Unlocked</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell style={{ minWidth: 160 }}>
                    <div>Action</div>
                    <Select
                      size="small"
                      variant="standard"
                      displayEmpty
                      fullWidth
                      value={pendingChatFilter}
                      onChange={(e) => updateHeaderFilter(setPendingChatFilter, "pendingChatFilter", e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="pending">Pending Chat</MenuItem>
                      <MenuItem value="none">No Pending Chat</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell style={{ minWidth: 220 }}>Last Message</TableCell>
                </>
              )}
              {(isExternalUser || isScmTeam) && (
                <>
                  <TableCell style={{ minWidth: 180 }}>
                    <MultiSelectFilterHeader
                      label="Shipshopy Disposition"
                      options={SHIPSHOPY_DISPOSITION_FILTER_OPTIONS}
                      selected={shipshopyDispositionFilter}
                      onApply={(vals) => updateHeaderFilter(setShipshopyDispositionFilter, "shipshopyDispositionFilter", vals)}
                      getLabel={shipshopyDispositionFilterLabel}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: 170 }}>
                    <MultiSelectFilterHeader
                      label="Shipshopy Chat"
                      options={CHAT_PENDING_FILTER_OPTIONS}
                      selected={shipshopyPendingChatFilter}
                      onApply={(vals) => updateHeaderFilter(setShipshopyPendingChatFilter, "shipshopyPendingChatFilter", vals)}
                      getLabel={chatPendingFilterLabel}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: 220 }}>Shipshopy Last Message</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((order, index) => (
              <TableRow key={order._id} hover>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{order.orderTypeLabel}</TableCell>
                <TableCell>{order.shippedBy}</TableCell>
                <TableCell
                  style={{ cursor: "pointer", color: "#1976d2", textDecoration: "underline" }}
                  onClick={() => window.open(`/crm/bulk/bulkorderdetails/${order._id}`, "_blank")}
                >
                  {order.zohoInvoice?.zohoInvoiceNumber}
                </TableCell>
                <TableCell>{order.awbNumber}</TableCell>
                <TableCell>{order.createdAt ? moment(order.createdAt).format("DD/MM/YYYY hh:mm A") : ""}</TableCell>
                <TableCell>{order.courierStatusText}</TableCell>
                <TableCell>{order.address?.name || order.name}</TableCell>
                <TableCell>{order.address?.phoneNumber || order.phoneNumber}</TableCell>
                <TableCell>{numericCommaSeparation(Math.round(order.totalPayableAmount || 0))}</TableCell>
                <TableCell>{numericCommaSeparation(Math.round(order.codAmount || 0))}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <RadCell order={order} editable={canEditRad} onUpdated={() => handleSearch(page, rowsPerPage)} />
                </TableCell>
                <TableCell>{order.ageingDays != null ? order.ageingDays : ""}</TableCell>
                {!isExternalUser && (
                  <>
                    <TableCell>
                      {order.shippingCostRtoFlag != null ? numericCommaSeparation(Math.round(order.shippingCostRtoFlag)) : ""}
                    </TableCell>
                    <TableCell>{order.agentName}</TableCell>
                    <TableCell>{order.managerName}</TableCell>
                    <TableCell>{order.verifiedByName}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DispositionCell
                        order={order}
                        disabled={!canEditDisposition}
                        onUpdated={() => handleSearch(page, rowsPerPage)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DashboardLockCell
                        order={order}
                        editable={canToggleDashboardLock}
                        onUpdated={() => handleSearch(page, rowsPerPage)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {/* Top-left badge = total messages in this chat, top-right = unread/pending for me */}
                      <Badge
                        badgeContent={order.totalMessageCount || 0}
                        color="primary"
                        overlap="rectangular"
                        anchorOrigin={{ vertical: "top", horizontal: "left" }}
                      >
                        <Badge badgeContent={order.pendingChatCount || 0} color="error" overlap="rectangular">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                            onClick={() => setChatOrderId(order._id)}
                          >
                            Chat
                          </Button>
                        </Badge>
                      </Badge>
                    </TableCell>
                    <TableCell style={{ minWidth: 220, verticalAlign: "bottom" }}>
                      <div style={{ display: "flex", flexDirection: "column", minHeight: 44 }}>
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", flexGrow: 1 }}>
                          {order.lastMessageText || ""}
                        </div>
                        {(order.lastMessageAt || order.lastMessageSenderName) && (
                          <Grid container justifyContent="space-between" alignItems="flex-end" wrap="nowrap" style={{ marginTop: 4 }}>
                            <Grid item style={{ fontSize: 11, color: "#9e9e9e", whiteSpace: "nowrap" }}>
                              {order.lastMessageAt ? moment(order.lastMessageAt).format("DD/MM/YYYY hh:mm A") : ""}
                            </Grid>
                            <Grid item style={{ fontSize: 11, color: "#757575", whiteSpace: "nowrap", paddingLeft: 8 }}>
                              {order.lastMessageSenderName || ""}
                            </Grid>
                          </Grid>
                        )}
                      </div>
                    </TableCell>
                  </>
                )}
                {(isExternalUser || isScmTeam) && order.isShipShopy === false && (
                  // RocketBox order — ShipShopy disposition / chat don't apply
                  <>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </>
                )}
                {(isExternalUser || isScmTeam) && order.isShipShopy !== false && (
                  <>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <ShipshopyDispositionCell
                        order={order}
                        disabled={!canEditShipshopyDisposition}
                        onUpdated={() => handleSearch(page, rowsPerPage)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Badge
                        badgeContent={order.shipshopyTotalMessageCount || 0}
                        color="primary"
                        overlap="rectangular"
                        anchorOrigin={{ vertical: "top", horizontal: "left" }}
                      >
                        <Badge badgeContent={order.shipshopyPendingChatCount || 0} color="error" overlap="rectangular">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                            onClick={() => setShipshopyChatOrderId(order._id)}
                          >
                            Chat
                          </Button>
                        </Badge>
                      </Badge>
                    </TableCell>
                    <TableCell style={{ minWidth: 220, verticalAlign: "bottom" }}>
                      <div style={{ display: "flex", flexDirection: "column", minHeight: 44 }}>
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", flexGrow: 1 }}>
                          {order.shipshopyLastMessageText || ""}
                        </div>
                        {(order.shipshopyLastMessageAt || order.shipshopyLastMessageSenderName) && (
                          <Grid container justifyContent="space-between" alignItems="flex-end" wrap="nowrap" style={{ marginTop: 4 }}>
                            <Grid item style={{ fontSize: 11, color: "#9e9e9e", whiteSpace: "nowrap" }}>
                              {order.shipshopyLastMessageAt ? moment(order.shipshopyLastMessageAt).format("DD/MM/YYYY hh:mm A") : ""}
                            </Grid>
                            <Grid item style={{ fontSize: 11, color: "#757575", whiteSpace: "nowrap", paddingLeft: 8 }}>
                              {order.shipshopyLastMessageSenderName || ""}
                            </Grid>
                          </Grid>
                        )}
                      </div>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalDocuments}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      <DeliveryCenterChatDrawer
        open={Boolean(chatOrderId)}
        orderId={chatOrderId}
        onClose={() => {
          setChatOrderId(null);
          // Opening the chat marks messages read server-side, so refresh this
          // row's pendingChatCount badge once the drawer closes.
          handleSearch(page, rowsPerPage);
        }}
      />

      <ShipshopyChatDrawer
        open={Boolean(shipshopyChatOrderId)}
        orderId={shipshopyChatOrderId}
        onClose={() => {
          setShipshopyChatOrderId(null);
          handleSearch(page, rowsPerPage);
        }}
      />
    </>
  );
}

function DeliveryCenterOrders() {
  // Deep-link support (?tab=rto) — used by the agent/TL dashboard RTO tiles to
  // land straight on the RTO Orders tab. Read once on mount; no live URL sync
  // needed since this page's own tab-switching stays purely in-component state
  // otherwise (matches how the rest of this file already works).
  const [tab, setTab] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('tab') === 'rto' ? 1 : 0;
    } catch (e) {
      return 0;
    }
  });

  return (
    <SubCard title="At Delivery Center / Undelivered / Out For Delivery Orders">
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Orders" />
        <Tab label="RTO Orders" />
      </Tabs>
      {tab === 0 && <DeliveryCenterOrdersTable onlyRto={false} />}
      {tab === 1 && <DeliveryCenterOrdersTable onlyRto={true} />}
    </SubCard>
  );
}

export default DeliveryCenterOrders;
